import { LitElement, html, css, nothing } from "lit";
import { customElement, state } from "lit/decorators.js";
import { lookupInvite, acceptInvite, type InviteLookup } from "../api/invites.js";
import { parseError, errorMessage } from "../api/errors.js";
import { currentUser, signOut } from "../api/auth.js";
import { pb } from "../api/client.js";
import { navigate } from "../nav.js";
import { toast } from "../components/index.js";

/**
 * page-invite (/invite?token=…) is the landing page for an emailed team
 * invitation. It is the one route that must work signed OUT — the link comes
 * from a mailbox, and the recipient may not have an account yet — so it is
 * deliberately unguarded and the lookup call carries no bearer token.
 *
 * Lookup first, always: it renders the team and role BEFORE asking anyone to
 * sign in, so nobody is sent through a login flow only to be told the link is
 * dead. From there:
 *   signed out → "Sign in to accept", carrying the invite URL in ?next= so the
 *                login flow (which may include an OTP signup) returns here;
 *   signed in  → Accept, which redeems the token and lands on the team.
 *
 * The three failure modes each get their own explicit state rather than a
 * toast, because each has a different way out: an expired or bogus token needs
 * a new invite from the team, while a mismatch just needs the right account —
 * so that state shows the (masked) invited address and offers to sign out.
 */
@customElement("page-invite")
export class PageInvite extends LitElement {
  @state() private loading = true;
  @state() private invite: InviteLookup | null = null;
  /** Terminal failure — a verdict about the invitation itself. */
  @state() private failCode = "";
  @state() private failText = "";
  /** Retryable lookup failure (network, 429, 5xx) — says nothing about the token. */
  @state() private loadError = "";
  @state() private accepting = false;
  /** Set when accept came back 403 email_mismatch (wrong account signed in). */
  @state() private mismatch = false;

  /**
   * The token from the query string, read once at construction. The router
   * matches on pathname only, so pages read location.search themselves.
   */
  private readonly token = new URLSearchParams(location.search).get("token") ?? "";

  static override styles = css`
    :host {
      display: flex;
      justify-content: center;
      align-items: flex-start;
      padding-top: clamp(var(--bino-space-lg), 8vh, 6rem);
    }
    .card {
      width: 100%;
      max-width: 27rem;
      box-sizing: border-box;
      background: var(--bino-surface);
      border: 1px solid var(--bino-border);
      border-radius: var(--bino-radius-lg);
      box-shadow: var(--bino-shadow-page);
      padding: var(--bino-space-xl);
      font-family: var(--bino-font-sans);
    }
    h1 {
      margin: 0 0 var(--bino-space-xs);
      font-size: 1.375rem;
      font-weight: 600;
      color: var(--bino-text);
      letter-spacing: -0.01em;
    }
    p {
      margin: 0 0 var(--bino-space-md);
      font-size: var(--bino-font-size-md);
      line-height: 1.5;
      color: var(--bino-text-secondary);
    }
    p:last-of-type {
      margin-bottom: 0;
    }
    .addr {
      font-family: var(--bino-font-mono);
      font-size: var(--bino-font-size-sm);
      color: var(--bino-text);
      word-break: break-all;
    }
    .role-line {
      display: flex;
      align-items: center;
      gap: var(--bino-space-xs);
      margin-bottom: var(--bino-space-md);
      font-size: var(--bino-font-size-md);
      color: var(--bino-text-secondary);
    }
    .actions {
      margin-top: var(--bino-space-lg);
      display: flex;
      flex-direction: column;
      gap: var(--bino-space-sm);
    }
    .foot {
      margin-top: var(--bino-space-md);
      font-size: var(--bino-font-size-base);
      color: var(--bino-text-secondary);
      text-align: center;
    }
  `;

  override connectedCallback() {
    super.connectedCallback();
    if (this.token === "") {
      // A link without a token can't be looked up — same dead end as a bad one.
      this.failCode = "invalid_token";
      this.failText = errorMessage("invalid_token");
      this.loading = false;
      return;
    }
    void this.load();
  }

  private async load() {
    this.loading = true;
    this.loadError = "";
    try {
      this.invite = await lookupInvite(this.token);
    } catch (err) {
      const { code, message } = parseError(err);
      // Only a verdict ABOUT the invitation is terminal. A dropped connection
      // or a 429 from the lookup brake says nothing about the token, and
      // rendering "the link may have been revoked, already used, or copied
      // incompletely" over one is worse than unhelpful: the invitee acts on it
      // and asks for a replacement, which REPLACES the pending row and kills
      // the link they were holding — the page's own false verdict destroying a
      // perfectly good invitation. Same discrimination accept() already makes.
      if (code === "invalid_token" || code === "invite_expired") {
        this.failCode = code;
        this.failText = errorMessage(code, message);
      } else {
        this.loadError = errorMessage(code, message);
      }
    } finally {
      this.loading = false;
    }
  }

  private async accept() {
    if (this.accepting) return;
    this.accepting = true;
    try {
      const { teamId } = await acceptInvite(this.token);
      toast(`You joined ${this.invite?.teamDisplayName ?? "the team"}`, "success");
      // replace: the invite URL is spent, so it must not sit in the back stack.
      navigate(`/teams/${teamId}`, { replace: true });
      // No accepting=false and no finally here: we are navigating away, and
      // updating state on a torn-down element only schedules a wasted render.
    } catch (err) {
      const { code, message } = parseError(err);
      if (code === "email_mismatch") {
        this.mismatch = true;
      } else if (code === "invalid_token" || code === "invite_expired") {
        // The invite died between lookup and accept (revoked, or just aged
        // out) — swap in the terminal state instead of toasting over a button
        // that can no longer work.
        this.failCode = code;
        this.failText = errorMessage(code, message);
      } else {
        toast(errorMessage(code, message), "error");
      }
      this.accepting = false;
    }
  }

  /** The sign-in detour, carrying this exact invite URL as the return path. */
  private loginHref(): string {
    return "/login?next=" + encodeURIComponent(location.pathname + location.search);
  }

  private switchAccount() {
    // Clear the wrong session first so /login shows the form instead of
    // bouncing straight back here as the same mismatched user.
    signOut();
    navigate(this.loginHref());
  }

  private fmtExpires(iso: string): string {
    if (!iso) return "";
    const d = new Date(iso);
    if (Number.isNaN(d.getTime())) return "";
    return d.toLocaleDateString(undefined, { year: "numeric", month: "short", day: "numeric" });
  }

  private renderFailure() {
    const expired = this.failCode === "invite_expired";
    return html`<div class="card">
      <h1>${expired ? "Invitation expired" : "Invitation not valid"}</h1>
      <p>${this.failText || errorMessage("invalid_token")}</p>
      ${expired
        ? nothing
        : html`<p>The link may have been revoked, already used, or copied incompletely.</p>`}
      <div class="actions">
        <bn-button variant="primary" block @click=${() => navigate("/")}>Go to bino Cloud</bn-button>
      </div>
    </div>`;
  }

  /**
   * The retryable counterpart to renderFailure: it makes no claim about the
   * invitation, and its primary action re-runs the lookup rather than sending
   * the invitee away. Mirrors the load-error + Retry pattern in page-security
   * and page-team.
   */
  private renderLoadError() {
    return html`<div class="card">
      <h1>Couldn't check this invitation</h1>
      <p>${this.loadError}</p>
      <p>This is not a problem with your link.</p>
      <div class="actions">
        <bn-button variant="primary" block @click=${() => void this.load()}>Try again</bn-button>
        <bn-button variant="secondary" block @click=${() => navigate("/")}
          >Go to bino Cloud</bn-button
        >
      </div>
    </div>`;
  }

  private renderMismatch(invite: InviteLookup) {
    const me = currentUser()?.email ?? "";
    return html`<div class="card">
      <h1>Wrong account</h1>
      <p>
        This invitation was sent to <span class="addr">${invite.emailMasked}</span>, but you're
        signed in as <span class="addr">${me}</span>.
      </p>
      <p>Sign in with the invited address to join ${invite.teamDisplayName}.</p>
      <div class="actions">
        <bn-button variant="primary" block @click=${() => this.switchAccount()}
          >Switch account</bn-button
        >
        <bn-button variant="secondary" block @click=${() => navigate("/")}>Not now</bn-button>
      </div>
    </div>`;
  }

  private renderInvite(invite: InviteLookup) {
    const authed = pb.authStore.isValid;
    const me = currentUser()?.email ?? "";
    const expires = this.fmtExpires(invite.expires);
    return html`<div class="card">
      <h1>Join ${invite.teamDisplayName}</h1>
      <div class="role-line">
        <span>Your role will be</span>
        <bn-badge variant="info">${invite.role}</bn-badge>
      </div>
      <p>
        The invitation was sent to <span class="addr">${invite.emailMasked}</span>. Sign in with that
        address to accept it.
      </p>
      <div class="actions">
        ${authed
          ? html`<bn-button
              variant="primary"
              block
              ?loading=${this.accepting}
              @click=${() => void this.accept()}
              >Accept invitation</bn-button
            >`
          : html`<bn-button variant="primary" block @click=${() => navigate(this.loginHref())}
              >Sign in to accept</bn-button
            >`}
      </div>
      ${authed
        ? html`<p class="foot">Signed in as <span class="addr">${me}</span></p>`
        : nothing}
      ${expires ? html`<p class="foot">This invitation expires on ${expires}.</p>` : nothing}
    </div>`;
  }

  override render() {
    if (this.loading) {
      return html`<bn-spinner block label="Checking invitation…"></bn-spinner>`;
    }
    if (this.failCode !== "") return this.renderFailure();
    if (this.loadError !== "") return this.renderLoadError();
    if (!this.invite) return this.renderFailure();
    if (this.mismatch) return this.renderMismatch(this.invite);
    return this.renderInvite(this.invite);
  }
}

declare global {
  interface HTMLElementTagNameMap {
    "page-invite": PageInvite;
  }
}
